'use client';

import React, { useRef, useEffect, useState } from 'react';

type Animation = 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right';

interface AnimateOnScrollProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  animation?: Animation;
}

const AnimateOnScroll: React.FC<AnimateOnScrollProps> = ({
  children,
  className = '',
  delay = 0,
  animation = 'fade-up',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const animationClass = {
    'fade-up': 'animate-on-scroll-fade-up',
    'fade-in': 'animate-on-scroll-fade-in',
    'slide-left': 'animate-on-scroll-slide-left',
    'slide-right': 'animate-on-scroll-slide-right',
  }[animation];

  return (
    <div
      ref={ref}
      className={`${animationClass} ${isVisible ? 'is-visible' : ''} ${className}`}
      style={{ animationDelay: isVisible ? `${delay}ms` : undefined }}
    >
      {children}
    </div>
  );
};

export default AnimateOnScroll;
